/**
 * Local-day helpers.
 *
 * The backend stores `local_date` on every log entry, computed from the user's
 * profile timezone at write time (see `backend/app/models/log.py`). The clients
 * must agree with it exactly — if the phone thinks it's still Tuesday and the
 * server has rolled to Wednesday, the user watches an entry vanish from today.
 *
 * So: never `new Date().toISOString().slice(0, 10)`. That gives the UTC date,
 * which is wrong for most of the world for part of every day.
 */

/** The user's calendar date for an instant, as `YYYY-MM-DD`. */
export function localDate(timeZone: string, at: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD, which is what the API expects. Doing this via
  // Intl rather than date arithmetic means DST and offset changes are handled
  // by the platform's tz database rather than by us.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/** Offset a `YYYY-MM-DD` by whole days. Parsed as UTC noon so a ±13h zone
 *  shift can't push the arithmetic onto the wrong day. */
export function shiftDate(isoDate: string, days: number): string {
  const anchor = new Date(`${isoDate}T12:00:00Z`);
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return anchor.toISOString().slice(0, 10);
}

/** The last `count` days ending at `endDate`, oldest first — the x-axis of any
 *  trend chart. */
export function dateRange(endDate: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => shiftDate(endDate, i - (count - 1)));
}

/** The device's own timezone, for seeding a new profile. */
export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The hour of day in a given zone, 0–23.
 *
 * Formatted rather than arithmetic on the epoch, because "what hour is it in
 * Zurich" is a question only the zone database can answer — offsets move twice
 * a year and the answer changes with them.
 */
export function localHour(timeZone: string, at: Date = new Date()): number {
  const hour = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    hour12: false,
  }).format(at);
  return Number(hour);
}

/** The wall-clock time in a zone, as `HH:MM`. */
export function localTime(timeZone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(at);
}

/**
 * How long after midnight a meal still counts as the night before.
 *
 * Four hours, and the number is a judgement rather than a measurement: past it,
 * late is just early. It is stated once here because the card that offers the
 * choice and the sentence that promises when the offer expires must not be able
 * to disagree.
 */
export const LATE_NIGHT_UNTIL_HOUR = 4;

/**
 * Whether we are in the window where "today" is genuinely ambiguous.
 *
 * A day is a stored local date, computed at write time and never recomputed.
 * That is right in every case except the one that happens most: eating at half
 * past midnight, where the app files supper under tomorrow without saying so
 * and the evening's numbers reset mid-meal.
 */
export function inLateNight(timeZone: string, at: Date = new Date()): boolean {
  return localHour(timeZone, at) < LATE_NIGHT_UNTIL_HOUR;
}

/**
 * The wall clock in a zone, as numbers.
 *
 * `hour` is taken modulo 24 because `hour12: false` renders midnight as "24"
 * on some engines — a documented V8 divergence that turns one hour a day into
 * an off-by-one if it is not handled.
 */
function zoneParts(timeZone: string, at: Date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") % 24,
    minute: get("minute"),
    second: get("second"),
  };
}

/** How far ahead of UTC a zone is at a given instant, in milliseconds. */
function zoneOffsetMs(timeZone: string, at: Date): number {
  const p = zoneParts(timeZone, at);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // The parts carry no milliseconds, so the instant is truncated to the second
  // before the two are subtracted — otherwise the offset is out by up to 999ms.
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * An instant that falls on `isoDate` in the user's zone.
 *
 * This is what a client sends as `logged_at` when it wants an entry filed under
 * a particular day. The API has no `local_date` field on the way in — the
 * server computes it from `logged_at` in the profile's zone and stores it, once
 * (`backend/app/api/v1/logs.py`). So a client that omits `logged_at` is not
 * saying "today", it is saying "now", and the two are only the same thing while
 * you are looking at today. That is the whole of the bug where backfilling
 * Tuesday's forgotten lunch landed it on Wednesday.
 *
 * The time of day is *now's* wall clock, carried across to the target date,
 * for two reasons: entries are ordered by `logged_at`, so a fixed hour would
 * make every backfilled row tie and shuffle; and a day's entries reading
 * 09:14, 13:02, 19:40 is a plausible record where four rows at noon is not.
 *
 * Today is returned as the current instant untouched, so the ordinary case
 * sends exactly what it always sent.
 */
export function instantOn(isoDate: string, timeZone: string, now: Date = new Date()): Date {
  if (localDate(timeZone, now) === isoDate) return now;

  const clock = zoneParts(timeZone, now);
  const wall = Date.parse(
    `${isoDate}T${pad(clock.hour)}:${pad(clock.minute)}:${pad(clock.second)}Z`,
  );

  // Twice, because the offset that applies at the *target* instant is not
  // necessarily the one in force now — a backfill across a DST boundary would
  // otherwise land an hour out, which at 00:30 is a different day.
  const first = wall - zoneOffsetMs(timeZone, new Date(wall));
  return new Date(wall - zoneOffsetMs(timeZone, new Date(first)));
}
